<script setup lang="ts">

  import * as v from 'valibot'
  import type { Form, FormSubmitEvent } from '#ui/types'

  // Idioma
  const { locale, t } = useI18n()

  useSeoMeta({ robots: 'noindex, nofollow' })

  // Layout
  definePageMeta({
    layout: 'simple'
  })

  // Validation
  const schema = v.object({
    email: v.pipe(
      v.string(),
      v.email(t('errors.login.email'))
    )
  })

  type Schema = v.InferOutput<typeof schema>

  const state = reactive({
    email: '',
    password: ''
  })

  const form = ref<Form<Schema>>()
  const error_message = ref<string>('')
  const server_message = ref<string>('')
  const recover_message = {
    es: 'Te hemos enviado un correo para recuperar tu contraseña',
    en: 'We have sent you an email to recover your password',
    fr: 'Nous vous avons envoyé un e-mail pour récupérer votre mot de passe',
    de: 'Wir haben Ihnen eine E-Mail geschickt, um Ihr Passwort zurückzusetzen',
    it: 'Ti abbiamo inviato un\'email per recuperare la tua password'
  }
  async function onSubmit(event: FormSubmitEvent<Schema>) {
    form.value!.clear()
    error_message.value = ''
    try {
      await $fetch(`/auth/byhours/recover`, {
        method: 'POST',
        body: {
          email: event.data.email,
          locale: locale.value
        }
      })
      server_message.value = recover_message[locale.value as keyof typeof recover_message]
    } catch (e: unknown) {
      const err = e as { data?: { statusMessage?: string }; statusMessage?: string }
      error_message.value = err?.data?.statusMessage || err?.statusMessage || ''
    }
  }

</script>

<template>
  <div class="w-full p-10">
    <div class="max-w-sm mx-auto flex flex-col gap-10">
      
      <div class="space-y-4">
        <h1 class="text-4xl text-center">{{ t('labels.login.recover.title') }}</h1>
        <p class="text-sm text-neutral-500 text-center">{{ t('labels.login.recover.subtitle') }}</p>
      </div>

      <UForm ref="form" :schema="schema" class="flex flex-col space-y-4" :state="state" @submit="onSubmit">
        <UFormField name="email">
          <UInput v-model="state.email" :placeholder="t('labels.login.placeholder.email')" size="xl"  />
        </UFormField>

        <UAlert
          v-if="error_message"
          icon="i-lucide-alert-circle"
          :title="error_message"
          color="error"
          variant="soft"
        />

        <UAlert
          v-if="server_message"
          icon="i-lucide-check-circle-2"
          :title="server_message"
          color="success"
          variant="soft"
        />

        <UButton 
          type="submit"
          class="justify-center bg-linear-to-r from-primary-500 to-pink-500"
          variant="solid"
          size="xl"
          :disabled="server_message ? true : false"
        >
          {{ t('labels.login.continue') }}
        </UButton>
      </UForm>

      <UButton
        :to="`/${locale}/login`"
        variant="link"
        size="xl"
        class="justify-center"
      >
        {{ t('labels.login.recover.back') }}
      </UButton>

    </div>
  </div>
</template>